const SOURCE_LIMITS = {
  alfano: {
    extension: 'zip',
    maxBytes: 50 * 1024 * 1024,
    contentTypes: new Set(['', 'application/zip', 'application/x-zip-compressed', 'application/octet-stream']),
  },
  iracing: {
    extension: 'ibt',
    maxBytes: 300 * 1024 * 1024,
    contentTypes: new Set(['', 'application/octet-stream']),
  },
}

const MAX_ARCHIVE_ENTRIES = 100
const MAX_ARCHIVE_ENTRY_BYTES = 50 * 1024 * 1024
const MAX_ARCHIVE_TOTAL_BYTES = 150 * 1024 * 1024

function getCrypto() {
  if (globalThis.crypto?.subtle) return globalThis.crypto
  throw new Error('Secure hashing is unavailable in this browser.')
}

async function toArrayBuffer(input) {
  if (typeof input === 'string') return new TextEncoder().encode(input).buffer
  if (input instanceof ArrayBuffer) return input
  if (ArrayBuffer.isView(input)) return input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength)
  if (input && typeof input.arrayBuffer === 'function') return input.arrayBuffer()
  throw new Error('Telemetry hash input must be bytes, text, or a File')
}

async function sha256Hex(input) {
  const digest = await getCrypto().subtle.digest('SHA-256', await toArrayBuffer(input))
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}

async function createContentAddressedTelemetryId(source, ownerId, sourceDigest) {
  if (!SOURCE_LIMITS[source]) throw new Error(`Unsupported telemetry source: ${source}`)
  if (!ownerId || typeof ownerId !== 'string') throw new Error('An authenticated telemetry owner is required')
  if (!/^[a-f0-9]{64}$/.test(sourceDigest)) throw new Error('A SHA-256 source digest is required')
  const ownerDigest = await sha256Hex(ownerId)
  return `${source}-${ownerDigest.slice(0, 16)}-${sourceDigest.slice(0, 40)}`
}

function validateTelemetryFile(file, source) {
  const limits = SOURCE_LIMITS[source]
  if (!limits) throw new Error(`Unsupported telemetry source: ${source}`)
  if (!file || typeof file.name !== 'string') throw new Error('A telemetry file is required')
  const suffix = `.${limits.extension}`
  if (!file.name.toLowerCase().endsWith(suffix)) {
    throw new Error(source === 'alfano' ? 'Please select an Alfano ZIP file' : 'Please select an iRacing IBT file')
  }
  const size = Number(file.size)
  if (!Number.isFinite(size) || size <= 0) throw new Error('The telemetry file is empty')
  if (size > limits.maxBytes) {
    throw new Error(`The telemetry file must be no larger than ${Math.round(limits.maxBytes / 1024 / 1024)} MB`)
  }
  const contentType = String(file.type || '').toLowerCase()
  if (!limits.contentTypes.has(contentType)) throw new Error('The telemetry file has an unsupported content type')
  return { extension: limits.extension, maxBytes: limits.maxBytes }
}

function validateArchiveManifest(entries) {
  if (!Array.isArray(entries) || entries.length === 0) throw new Error('The Alfano archive is empty')
  if (entries.length > MAX_ARCHIVE_ENTRIES) throw new Error('The Alfano archive contains too many entries')

  let totalBytes = 0
  let hasTelemetry = false
  entries.forEach(entry => {
    const name = String(entry.name || '')
    const normalized = name.replace(/\\/g, '/')
    if (!name || normalized.startsWith('/') || normalized.split('/').includes('..')) {
      throw new Error('The Alfano archive contains an unsafe path')
    }
    if (entry.encrypted) throw new Error('Encrypted Alfano archives are not supported')
    if (entry.dir) return
    const size = Number(entry.uncompressedSize)
    if (!Number.isFinite(size) || size < 0) throw new Error('The Alfano archive contains invalid size metadata')
    if (size > MAX_ARCHIVE_ENTRY_BYTES) throw new Error('An Alfano archive entry is too large')
    totalBytes += size
    if (totalBytes > MAX_ARCHIVE_TOTAL_BYTES) throw new Error('The expanded Alfano archive is too large')
    if (/\.(csv|txt)$/i.test(normalized)) hasTelemetry = true
  })

  if (!hasTelemetry) throw new Error('No CSV or TXT telemetry file was found in the Alfano archive')
  return { entryCount: entries.length, totalBytes }
}

module.exports = {
  MAX_ARCHIVE_ENTRIES,
  MAX_ARCHIVE_ENTRY_BYTES,
  MAX_ARCHIVE_TOTAL_BYTES,
  SOURCE_LIMITS,
  createContentAddressedTelemetryId,
  sha256Hex,
  validateArchiveManifest,
  validateTelemetryFile,
}
